import React, {
    forwardRef,
    useCallback,
    useImperativeHandle,
    useRef
} from 'react';
import HBTag from '../HBTag/HBTag';
import type { DSFieldTag } from './HBTextField.types';

export interface TagsInputRowRef {
    /** Focuses the delete button of the last tag. Call this when ArrowLeft fires at cursor position 0 in the parent field. */
    focusLast: () => void;
}

export interface TagsInputRowProps {
    tags: DSFieldTag[];
    /** When provided, renders a delete button on each tag and enables Delete/Backspace key removal. */
    onTagRemove?: (id: string) => void;
    /** Called when ArrowRight on the last tag should return focus to the field input. */
    onNavigateToInput: () => void;
    /** data-testid prefix forwarded to each HBTag — results in `{prefix}--tag-{id}`. */
    testIdPrefix: string;
}

const TagsInputRow = forwardRef<TagsInputRowRef, TagsInputRowProps>(
    ({ tags, onTagRemove, onNavigateToInput, testIdPrefix }, ref) => {
        const wrapperRefs = useRef<(HTMLDivElement | null)[]>([]);

        // Keep a ref to tags so callbacks that close over it never go stale.
        const tagsRef = useRef(tags);
        tagsRef.current = tags;

        const focusDeleteAt = useCallback((index: number) => {
            wrapperRefs.current[index]
                ?.querySelector<HTMLButtonElement>('button[type="button"]')
                ?.focus();
        }, []);

        useImperativeHandle(
            ref,
            () => ({
                focusLast: () => focusDeleteAt(tagsRef.current.length - 1)
            }),
            [focusDeleteAt]
        );

        const handleTagKeyDown = useCallback(
            (e: React.KeyboardEvent, index: number, tagId: string) => {
                if (e.key === 'ArrowLeft') {
                    e.preventDefault();
                    if (index > 0) {
                        focusDeleteAt(index - 1);
                    }
                } else if (e.key === 'ArrowRight') {
                    e.preventDefault();
                    if (index < tagsRef.current.length - 1) {
                        focusDeleteAt(index + 1);
                    } else {
                        onNavigateToInput();
                    }
                } else if (
                    e.key === 'Delete' ||
                    e.key === 'Backspace' ||
                    e.key === 'Enter' ||
                    e.key === ' '
                ) {
                    // preventDefault on Enter/Space blocks the browser's synthetic click
                    // so onTagRemove is not called twice.
                    e.preventDefault();
                    // Focus before removing so focus is never lost.
                    // Priority: previous tag → next tag → input.
                    if (index > 0) {
                        focusDeleteAt(index - 1);
                    } else if (index < tagsRef.current.length - 1) {
                        focusDeleteAt(index + 1);
                    } else {
                        onNavigateToInput();
                    }
                    onTagRemove?.(tagId);
                }
            },
            [focusDeleteAt, onTagRemove, onNavigateToInput]
        );

        return (
            <>
                {tags.map((tag, index) => (
                    <div
                        key={tag.id}
                        ref={el => {
                            wrapperRefs.current[index] = el;
                        }}
                        style={{ display: 'contents' }}
                    >
                        <HBTag
                            label={tag.label}
                            size="sm"
                            onDelete={
                                onTagRemove
                                    ? e => {
                                          e.stopPropagation();
                                          onTagRemove(tag.id);
                                      }
                                    : undefined
                            }
                            deletableProps={
                                onTagRemove
                                    ? {
                                          tabIndex: -1,
                                          onKeyDown: e =>
                                              handleTagKeyDown(e, index, tag.id)
                                      }
                                    : undefined
                            }
                            data-testid={`${testIdPrefix}--tag-${tag.id}`}
                        />
                    </div>
                ))}
            </>
        );
    }
);

TagsInputRow.displayName = 'TagsInputRow';

export default TagsInputRow;
